import React from 'react';
import TouchDragInput from './TouchDragInput';

const UProfileVisual = ({ 
  base, leftSide, rightSide, thickness, leftLip, rightLip, 
  onBaseChange, onLeftChange, onRightChange, onLeftBlur, onRightBlur, 
  onThicknessChange, onLeftLipChange, onRightLipChange, onLeftLipBlur, onRightLipBlur, 
  snapToValidSide, getNextValidSide, getNextValidLip, getNextValidThickness,
  currentThickness, currentBase  // Use these for validation to avoid stale closures
}) => {
  // Calculate proportional dimensions for visualization
  const baseValue = parseFloat(base) || 30;
  const leftValue = parseFloat(leftSide) || 30;
  const rightValue = parseFloat(rightSide) || 30;
  const thicknessValue = parseFloat(thickness) || 2;
  const leftLipValue = parseFloat(leftLip) || 0;
  const rightLipValue = parseFloat(rightLip) || 0;
  
  // Responsive scale - smaller on mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const scale = isMobile ? 4 : 6;
  
  const leftHeight = leftValue * scale;
  const rightHeight = rightValue * scale;
  const baseWidth = baseValue * scale;
  const thicknessWidth = Math.max(thicknessValue * scale, isMobile ? 6 : 8);
  const leftLipWidth = leftLipValue * scale;
  const rightLipWidth = rightLipValue * scale;
  
  const maxDimension = Math.max(leftHeight, rightHeight);

  // Responsive sizing
  const inputWidth = isMobile ? 'w-14' : 'w-16';
  // Reduced offsets since +/- buttons are removed
  const totalWidth = baseWidth + (isMobile ? 140 : 180);
  const profileLeftOffset = isMobile ? 70 : 90;

  return (
    <div className="flex justify-center overflow-x-auto py-2">
      <div className="relative" style={{ width: totalWidth, height: maxDimension + (isMobile ? 90 : 110) }}>
        
        {/* Thickness input - TOP CENTER */}
        <div className="absolute" style={{ left: '50%', top: 0, transform: 'translateX(-50%)' }}>
          <TouchDragInput
            label="Thickness"
            value={thickness}
            onChange={onThicknessChange}
            step={0.1}
            min={0.8}
            max={2.0}
            className={`${inputWidth} text-center text-sm`}
            placeholder="2"
            validationFn={getNextValidThickness}
            validationParams={[]}
          />
        </div>

        {/* U-Profile Shape with inputs */}
        <div className="absolute" style={{ top: isMobile ? 45 : 55, left: profileLeftOffset, width: baseWidth, height: maxDimension }}>
          {/* Left Side */}
          <div 
            className="absolute bg-gradient-to-r from-slate-600 to-slate-500 rounded-t-lg shadow-lg"
            style={{ 
              left: 0, 
              bottom: 0, 
              width: thicknessWidth, 
              height: leftHeight,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 2px 0 8px rgba(0,0,0,0.2)'
            }}
          >
            {/* Left Side Input */}
            <div className="absolute" style={{ left: isMobile ? -58 : -70, top: '50%', transform: 'translateY(-50%)' }}>
              <TouchDragInput
                label="L Side"
                value={leftSide}
                onChange={onLeftChange}
                onBlur={onLeftBlur}
                step={0.5}
                min={5}
                max={60}
                className={`${inputWidth} text-center text-sm`}
                placeholder="29"
                validationFn={getNextValidSide}
                validationParams={[currentThickness || thickness]}
              />
            </div>
          </div>
          
          {/* Left Lip (if > 0) — measured from the profile's OUTER edge (wall
              included), so base 40 with two 20 lips meets exactly at center */}
          {leftLipWidth > 0 && (
            <div 
              className="absolute bg-gradient-to-b from-slate-500 to-slate-400 rounded-r-lg shadow-lg"
              style={{ 
                left: 0, 
                top: maxDimension - leftHeight, 
                width: leftLipWidth, 
                height: thicknessWidth,
                boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.15)'
              }}
            />
          )}
          
          {/* L Lip Input - just outside left side bar at top */}
          <div 
            className="absolute" 
            style={{ 
              left: isMobile ? -58 : -70,
              top: maxDimension - leftHeight - (isMobile ? 42 : 48)
            }}
          >
            <TouchDragInput
              label="L Lip"
              value={leftLip}
              onChange={onLeftLipChange}
              onBlur={onLeftLipBlur}
              step={0.5}
              min={0}
              max={30}
              className={`${inputWidth} text-center text-sm`}
              placeholder="0"
              validationFn={getNextValidLip}
              validationParams={[currentThickness || thickness, currentBase || base]}
              dragDirection="horizontal"
            />
          </div>
          
          {/* Right Side */}
          <div 
            className="absolute bg-gradient-to-r from-slate-500 to-slate-600 rounded-t-lg shadow-lg"
            style={{ 
              right: 0, 
              bottom: 0, 
              width: thicknessWidth, 
              height: rightHeight,
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), -2px 0 8px rgba(0,0,0,0.2)'
            }}
          >
            {/* Right Side Input */}
            <div className="absolute" style={{ right: isMobile ? -58 : -70, top: '50%', transform: 'translateY(-50%)' }}>
              <TouchDragInput
                label="R Side"
                value={rightSide}
                onChange={onRightChange}
                onBlur={onRightBlur}
                step={0.5}
                min={5}
                max={60}
                className={`${inputWidth} text-center text-sm`}
                placeholder="29"
                validationFn={getNextValidSide}
                validationParams={[currentThickness || thickness]}
              />
            </div>
          </div>
          
          {/* Right Lip (if > 0) — measured from the outer edge, mirrors left */}
          {rightLipWidth > 0 && (
            <div 
              className="absolute bg-gradient-to-b from-slate-500 to-slate-400 rounded-l-lg shadow-lg"
              style={{ 
                right: 0, 
                top: maxDimension - rightHeight, 
                width: rightLipWidth, 
                height: thicknessWidth,
                boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.2), 0 2px 6px rgba(0,0,0,0.15)'
              }}
            />
          )}
          
          {/* R Lip Input - just outside right side bar at top */}
          <div 
            className="absolute" 
            style={{ 
              right: isMobile ? -58 : -70,
              top: maxDimension - rightHeight - (isMobile ? 42 : 48)
            }}
          >
            <TouchDragInput
              label="R Lip"
              value={rightLip}
              onChange={onRightLipChange}
              onBlur={onRightLipBlur}
              step={0.5}
              min={0}
              max={30}
              className={`${inputWidth} text-center text-sm`}
              placeholder="0"
              validationFn={getNextValidLip}
              validationParams={[currentThickness || thickness, currentBase || base]}
              dragDirection="horizontal-reverse"
            />
          </div>
          
          {/* Base (bottom) */}
          <div 
            className="absolute bg-gradient-to-b from-slate-500 to-slate-600 rounded-b-lg shadow-lg"
            style={{ 
              left: 0, 
              bottom: 0, 
              width: baseWidth, 
              height: thicknessWidth,
              boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.25)'
            }}
          >
            {/* Base Input */}
            <div className="absolute" style={{ left: '50%', bottom: isMobile ? -42 : -50, transform: 'translateX(-50%)' }}>
              <TouchDragInput
                label="Base"
                value={base}
                onChange={onBaseChange}
                step={0.5}
                min={20}
                max={60}
                className={`${inputWidth} text-center text-sm`}
                placeholder="31"
                dragDirection="horizontal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UProfileVisual;
